import { useMemo, useCallback, useState } from "react";
import { useAdmin } from "../context/AdminContext";
import { usePreviewSafeData } from "../hooks/usePreviewSafeData";

// Dispatched on `window` when an admin edit could not be written to the
// database at all (both the in-place UPDATE and the fallback INSERT failed).
// AdminBanner listens for it and shows the admin a failure toast.
export const MC_SAVE_FAILED_EVENT = "happyspace-mc-save-failed";

// Broadcast a write that reached NEITHER the update nor the insert path, so the
// admin gets told instead of being shown a phantom edit that only ever existed
// in their own browser. AdminBanner renders these as a visible toast.
function notifySaveFailed(item: any, err: any) {
  try {
    window.dispatchEvent(
      new CustomEvent(MC_SAVE_FAILED_EVENT, {
        detail: {
          name: item?.name || item?.title || "This card",
          message: err?.message || String(err),
        },
      }),
    );
  } catch {
    /* non-browser env (SSR/prerender) */
  }
}

/**
 * useManagedCollection — turns any list of hardcoded "default" items into a
 * fully admin-manageable collection without bulk-migrating the defaults into
 * the database.
 *
 * The effective list = code defaults ⊕ store changes. The store (a dedicated
 * `mc_<slug>` datatable, partitioned by businessId like every other table)
 * holds two kinds of rows:
 *   - kind:"override"  — an edit / hide / delete applied to a default, keyed by
 *                        `refId` (the default's stable id).
 *   - kind:"custom"    — a brand-new item the admin added.
 *
 * Public visitors see only visible (non-hidden, non-removed) items and need no
 * DB at all if nothing was changed. Admins see everything, with hidden/removed
 * items flagged. Every mutation persists to the store and reflects immediately
 * for both the admin view and the public site.
 */

export interface ManagedItem {
  _mcId: string; // stable id used as the override refId (NOT unique — see _key)
  /**
   * UNIQUE React key for this card. `_mcId` is deliberately not unique: most
   * pages derive it with slugify(name), so cards sharing a name collide (both
   * Swadeshi branches -> "swadeshi", three Indopak rows -> "indopak-super-market").
   * Using a duplicated value as a React key makes reconciliation undefined —
   * React warns "may cause children to be duplicated and/or omitted", and in
   * practice DELETED cards stayed mounted in the DOM after the override data
   * loaded and filtered them out of the list. Always key rendered cards on
   * `_key`; keep using `_mcId` for override lookups.
   */
  _key: string;
  _isCustom: boolean; // true for admin-added items
  _hidden: boolean;
  _removed: boolean;
  _order: number; // effective sort position (stored order ?? natural index)
  _rowId: string | null; // store row _id for custom items / existing overrides
  [key: string]: any;
}

// `order` is reserved for drag-sort and kept out of the card's own fields.
// `_savedAt` is a client-stamped write time (see persistChange) used for
// newest-wins dedup — also a meta key, never a card field.
const META_KEYS = new Set(["kind", "refId", "hidden", "removed", "order", "_savedAt", "_optKey", "_id", "id", "createdAt", "updatedAt", "data", "businessId", "tableId", "__v", "source", "status", "attachments"]);

// The data SDK may hand back rows FLATTENED ({ refId, order, _id }) or in the RAW
// datarow shape ({ _id, data: { refId, order } }). Merge any nested `data` up so
// field access works either way (top-level envelope wins for _id/timestamps).
/**
 * The platform's data API SILENTLY STRIPS empty-string fields on write. Proven
 * against the live endpoint: POSTing {kind:"custom",name:"empty-probe",phone:""}
 * stores {kind:"custom",name:"empty-probe"} — the `phone` key is simply gone.
 *
 * That makes CLEARING a field impossible through the admin UI. The edit modal
 * correctly sends `instagram: ""`, the API drops it, so the new override row has
 * no opinion about `instagram` at all — and the read-merge below (which only
 * applies keys that are not `undefined`, so a newer partial row can't wipe an
 * older edit) inherits the OLD value from an earlier override row. The admin
 * removes a link, saves, and the icon is still there.
 *
 * Fix: persist a cleared field as this sentinel instead of "". It is a real
 * non-empty string, so the API stores it, and it round-trips back to "" here in
 * flattenRow — before the override merge, the field strip, or any consumer sees
 * it. So a cleared field becomes a genuine "" that correctly beats older rows.
 */
export const CLEARED_SENTINEL = "__hs_cleared__";

const uncleared = (v: any) => (v === CLEARED_SENTINEL ? "" : v);

function flattenRow(r: any): any {
  if (r && typeof r.data === "object" && r.data) {
    const { data, ...rest } = r;
    const merged: Record<string, any> = { ...data, ...rest };
    for (const k of Object.keys(merged)) merged[k] = uncleared(merged[k]);
    return merged;
  }
  if (r && typeof r === "object") {
    const out: Record<string, any> = { ...r };
    for (const k of Object.keys(out)) out[k] = uncleared(out[k]);
    return out;
  }
  return r;
}

function stripMeta(row: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(row)) {
    if (!META_KEYS.has(k)) out[k] = v;
  }
  return out;
}

export function slugify(s: string): string {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}

export interface ManagedCollection<T> {
  slug: string;
  items: (T & ManagedItem)[];
  visibleCount: number;
  totalCount: number;
  isAdmin: boolean;
  adminMode: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
  addItem: (fields: Record<string, any>) => Promise<void>;
  saveEdit: (item: ManagedItem, fields: Record<string, any>) => Promise<void>;
  deleteItem: (item: ManagedItem) => Promise<void>;
  toggleHidden: (item: ManagedItem) => Promise<void>;
  restoreItem: (item: ManagedItem) => Promise<void>;
  /**
   * Reset a DEFAULT (non-custom) item's fields back to the code default,
   * clearing any stuck admin override (e.g. an old image from a prior edit).
   * No-op for admin-added (`_isCustom`) items — there's no code default to
   * fall back to. Pass `fieldKeys` (the page's field-schema keys) so every
   * editable field is explicitly reset, not just the ones already present.
   */
  resetToDefault: (item: ManagedItem, fieldKeys?: string[]) => Promise<void>;
  /**
   * Drag-sort: place `draggedId` immediately before (`before=true`) or after
   * (`before=false`) `targetId`, and persist. `before` is derived from where the
   * card was actually dropped, so placement is exact regardless of direction.
   */
  moveItem: (draggedId: string, targetId: string, before?: boolean) => Promise<void>;
  /**
   * Drag-sort (SWAP): exchange the positions of two items — they trade places and
   * every other item stays exactly where it is.
   */
  swapItems: (aId: string, bId: string) => Promise<void>;
}

export function useManagedCollection<T extends Record<string, any>>(
  slug: string,
  defaults: T[],
  getDefaultId: (item: T, index: number) => string
): ManagedCollection<T> {
  const storeSlug = `mc_${slug}`;
  // usePreviewSafeData talks to the REAL backend table in every environment
  // (this preview sandbox AND the deployed site) and — critically — every
  // `mc_*` table is now in its shared-session allowlist (see
  // hooks/usePreviewSafeData.ts). That's what actually fixes "I added two
  // cards from Admin but I don't see them": the raw useData() hook scopes
  // reads to the CALLER's own random per-browser session id, so a card
  // inserted from an admin session was invisible to the public page (a
  // different session) and even to the same admin after a hard refresh
  // issued a new session. The shared deterministic bucket means every
  // reader/writer of mc_<slug> resolves to the identical rows regardless of
  // which browser/session touched it. localStorage is kept only as this
  // hook's own last-resort buffer for a genuinely failed network write —
  // never as the primary source of truth.
  // bufferOnWriteFailure:false — a managed collection must NEVER fall back to
  // a localStorage echo. It has a real cross-session-safe fallback (INSERT a
  // fresh override row, see persistChange), but that can only run if the hook
  // actually reports the failure instead of silently buffering it.
  const sdk = usePreviewSafeData(storeSlug, { bufferOnWriteFailure: false });
  const { isAdmin, adminMode } = useAdmin();

  const rows = sdk.rows;
  const loading = sdk.loading;
  const refetch = sdk.refetch;
  const insert = sdk.insert;
  const update = sdk.update;
  const remove = sdk.remove;

  // Optimistic overlay keyed by _mcId. Each mutation (edit / hide / delete /
  // restore / reorder) writes its change here IMMEDIATELY so the card updates
  // instantly — even if the platform SDK doesn't re-render the list after the
  // write (the write still persists in the background). Entries hold plain field
  // patches (name/image/…) and/or meta keys `_hidden` / `_removed` / `_order`.
  // On reload the stored data is the source of truth.
  const [opt, setOpt] = useState<Record<string, Record<string, any>>>({});
  // Optimistic newly-added items (shown instantly; deduped once the real row
  // arrives, matched by the `_optKey` we stamp on insert).
  const [optAdds, setOptAdds] = useState<any[]>([]);
  // Row ids we learned from our OWN inserts this session, keyed by _mcId. The
  // platform SDK doesn't always surface a row id on refetched rows, which made
  // every follow-up reorder/edit INSERT a duplicate override instead of updating
  // the existing one. Remembering the id returned by insert() lets the next write
  // target the same row — so reorders persist cleanly instead of piling up dupes.
  const [localRowIds, setLocalRowIds] = useState<Record<string, string>>({});
  const rememberRowId = useCallback((mcId: string, res: any) => {
    const id = res && (res.id ?? res._id ?? res.insertedId ?? res.rowId);
    if (id != null && String(id))
      setLocalRowIds((p) => (p[mcId] === String(id) ? p : { ...p, [mcId]: String(id) }));
  }, []);

  // `update`/`remove` above already route preview-mode calls through our own
  // localStorage-backed store (readLocalStore/writeLocalStore), not the
  // platform's real network endpoint — so there's no doomed network call left
  // to guard against here. The old versions of these wrappers still had an
  // `if (isPreviewMode()) return;` short-circuit from before that local-store
  // path existed, which silently no-op'd every preview delete/update (e.g. a
  // link edit or card delete would "save" in the optimistic UI overlay but
  // never reach writeLocalStore, so it reverted on refresh). Just delegate.
  const safeUpdate = useCallback(
    async (rid: string, fields: Record<string, any>) => {
      await update(rid, fields);
    },
    [update]
  );
  const safeRemove = useCallback(
    async (rid: string) => {
      await remove(rid);
    },
    [remove]
  );

  // Row id may come back under several field names depending on the SDK (local
  // shim: `id`/`_id`; platform has used others). Check them all; null only if
  // truly absent.
  const rowId = (r: any): string | null => {
    const v = r && (r.id ?? r._id ?? r.rowId ?? r._rowId ?? r.documentId ?? r.key);
    return v != null && String(v) ? String(v) : null;
  };
  // Best-effort recency: prefer explicit timestamps, else fall back to the time
  // embedded in a 24-hex ObjectId. This makes "newest override wins" DETERMINISTIC
  // even when the platform SDK omits updatedAt/createdAt — so a reorder never
  // reverts on reload by picking a stale duplicate.
  const objectIdTime = (idLike: any): number => {
    const s = String(idLike || "");
    return /^[0-9a-fA-F]{24}$/.test(s) ? parseInt(s.slice(0, 8), 16) * 1000 : 0;
  };
  // Newest-wins ordering. Prefer `_savedAt` — a client-stamped write time we
  // put INSIDE the override's data (see persistChange), so it round-trips
  // through ANY backend and reflects the real write time even for in-place
  // updates (where the row's createdAt stays old). Fall back to server
  // updatedAt/createdAt, then to the ObjectId's embedded time. Without this,
  // an in-place-updated override (old createdAt, newest data) could lose the
  // dedup to a stale duplicate with a newer createdAt — making an admin's
  // latest edit silently revert to an older value on the public site.
  const recency = (r: any): number =>
    Number(r?._savedAt) ||
    new Date(r?.updatedAt || r?.createdAt || 0).getTime() ||
    objectIdTime(r?._id ?? r?.id);

  const { overrides, customs } = useMemo(() => {
    const groups: Record<string, any[]> = {};
    const customs: any[] = [];
    for (const raw of rows || []) {
      const r = flattenRow(raw);
      if (r.kind === "override" && r.refId) (groups[r.refId] = groups[r.refId] || []).push(r);
      else if (r.kind === "custom") customs.push(r);
    }
    // Duplicate override rows for one refId accumulate (the platform inserts a
    // fresh override on every edit/reorder because cross-session UPDATE is
    // rejected). Rather than pick a single newest "winner" wholesale — which
    // let a newer PARTIAL override (e.g. an order-only change from a drag, or a
    // hide toggle, carrying NO card fields) erase earlier field edits back to
    // defaults — MERGE all rows for a refId oldest→newest so each newer row
    // only overwrites the specific keys it actually contains. A field edited at
    // T1 therefore survives a reorder at T2. This also makes overrides[refId]
    // the COMPLETE effective data, so a fresh override built from it (see
    // persistChange) is always self-contained.
    const overrides: Record<string, any> = {};
    for (const refId of Object.keys(groups)) {
      const list = groups[refId].slice().sort((a, b) => recency(a) - recency(b));
      const merged: Record<string, any> = {};
      for (const r of list) for (const [k, v] of Object.entries(r)) if (v !== undefined) merged[k] = v;
      overrides[refId] = merged;
    }
    return { overrides, customs };
  }, [rows]);

  const toOrder = (v: any, fallback: number): number => {
    const n = Number(v);
    return v != null && Number.isFinite(n) ? n : fallback;
  };

  const allItems = useMemo(() => {
    // Apply the optimistic overlay last so pending edits/hide/delete/order win.
    const withOpt = (item: any, key: string) => (opt[key] ? { ...item, ...opt[key] } : item);
    const keySeen: Record<string, number> = {};
    // Dedup suffix must be URL-safe: `#` would be read as a URL fragment
    // (everything after it never reaches the router), so use `-2`, `-3`, …
    const uniqueKey = (base: string) => {
      const n = (keySeen[base] = (keySeen[base] || 0) + 1);
      return n === 1 ? base : `${base}-${n}`;
    };
    const defaultItems = (defaults || []).map((d, i) => {
      const id = getDefaultId(d, i);
      const ov = overrides[id];
      const merged = ov ? { ...d, ...stripMeta(ov) } : { ...d };
      return withOpt(
        {
          ...merged,
          _mcId: id,
          _key: uniqueKey(String((d as any).id || id)),
          _isCustom: false,
          _hidden: !!(ov && ov.hidden),
          _removed: !!(ov && ov.removed),
          _order: ov ? toOrder(ov.order, i) : i,
          _rowId: (ov && rowId(ov)) || localRowIds[id] || null,
        },
        id
      ) as T & ManagedItem;
    });
    const base = defaults?.length || 0;
    const customItems = customs.map((c, j) => {
      const cid = rowId(c) || "";
      // A custom (admin-added) card = its own kind:"custom" base row PLUS any
      // kind:"override" rows keyed by refId=cid (hide / delete / edit / order),
      // exactly like a default card. This lets hide/delete/unhide/edit persist
      // via override INSERTs — which the platform always allows — instead of
      // update/remove of the base row, which the platform rejects cross-session
      // (that was why deleting/hiding an added card silently failed).
      const ov = overrides[cid];
      const merged = ov ? { ...stripMeta(c), ...stripMeta(ov) } : { ...stripMeta(c) };
      // Admin-added cards link to their detail page via `_key`. Use the
      // slugified card name (e.g. "Dr. Aisha Khan" -> "dr-aisha-khan") so URLs
      // are human-readable and SEO-friendly, NOT the raw Mongo row id. Fall
      // back to the row id only if the card somehow has no name/title.
      const customSlug = slugify(String(merged.name || merged.title || ""));
      return withOpt(
        {
          ...merged,
          _mcId: cid,
          _key: uniqueKey(customSlug || String(cid || "")),
          _isCustom: true,
          _hidden: !!(ov && ov.hidden !== undefined ? ov.hidden : c.hidden),
          _removed: !!(ov && ov.removed),
          _order: toOrder(ov && ov.order !== undefined ? ov.order : c.order, base + j),
          // Target override rows for writes (not the base custom row); null until
          // the first override exists, so the first change INSERTs one.
          _rowId: (ov && rowId(ov)) || localRowIds[cid] || null,
          _customRowId: cid,
        },
        cid
      ) as T & ManagedItem;
    });
    // Stable sort by effective order so admin reordering reflects everywhere.
    // Optimistic pending adds — only those not yet present as a real row.
    const realKeys = new Set(customs.map((c: any) => c._optKey).filter(Boolean));
    const pendingItems = optAdds
      .filter((a) => !realKeys.has(a._optKey))
      .map((a, k) => ({
        ...a,
        _mcId: `pending-${a._optKey}`,
        _key: uniqueKey(slugify(String(a.name || a.title || "")) || a._optKey),
        _isCustom: true,
        _hidden: false,
        _removed: false,
        _order: 1e9 + k,
        _rowId: null,
      })) as (T & ManagedItem)[];
    return [...defaultItems, ...customItems, ...pendingItems].sort((a, b) => a._order - b._order);
  }, [defaults, overrides, customs, getDefaultId, opt, optAdds, localRowIds]);

  const visibleItems = useMemo(
    () => allItems.filter((it) => !it._hidden && !it._removed),
    [allItems]
  );

  const items = adminMode ? allItems : visibleItems;

  const addItem = useCallback(
    async (fields: Record<string, any>) => {
      const optKey = `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
      setOptAdds((p) => [...p, { _optKey: optKey, ...fields }]); // instant
      try {
        await insert({ kind: "custom", hidden: false, _optKey: optKey, ...fields });
      } catch (err) {
        // The new card never reached the database — drop the optimistic entry
        // so the admin doesn't keep looking at a card no visitor can see.
        setOptAdds((p) => p.filter((a) => a._optKey !== optKey));
        notifySaveFailed(fields, err);
        throw err;
      }
    },
    [insert]
  );

  // Resolve the row to write to: the item's own id, else one we remembered from a
  // prior insert this session (covers the platform's missing-row-id case).
  const resolveRowId = useCallback(
    (item: ManagedItem): string | null => item._rowId || localRowIds[item._mcId] || null,
    [localRowIds]
  );

  // Fields that belong to the raw row ENVELOPE (not the override payload) —
  // excluded when copying an existing override's data into a fresh insert.
  const ENVELOPE = new Set(["_id", "id", "tableId", "businessId", "sessionId", "sourceIp", "userAgent", "referer", "status", "attachments", "createdAt", "updatedAt", "__v", "data", "_optKey", "_savedAt"]);
  const overrideData = (ov: any): Record<string, any> => {
    const o: Record<string, any> = {};
    if (ov) for (const [k, v] of Object.entries(ov)) if (!ENVELOPE.has(k)) o[k] = v;
    return o;
  };

  // After a write we keep ONE row per override (the just-written "keeper", which
  // holds the complete merged data) and delete this card's other override rows,
  // so duplicates never pile up (unbounded pile-up eventually exceeds the
  // platform's read limit and edits appear to revert on refresh). Best-effort:
  // the platform only lets a session delete rows IT created, so cross-session
  // rows may survive — that's fine, the pile stays small and bounded, and the
  // read merge still resolves the newest correctly.
  const pruneOverrides = useCallback(
    async (refId: string, keepId: string | null) => {
      if (!keepId) return; // never prune if the keeper write didn't confirm
      const ids = (rows || [])
        .map(flattenRow)
        .filter((r: any) => r.kind === "override" && r.refId === refId)
        .map((r: any) => rowId(r))
        .filter((id): id is string => !!id && String(id) !== String(keepId));
      for (const id of ids) {
        try {
          await remove(id);
        } catch {
          /* cross-session delete rejected — ignore */
        }
      }
    },
    [rows, remove]
  );

  // Persist a change to an item's override. IMPORTANT platform constraint: a
  // session may only UPDATE rows IT created — updating a row created by another
  // session (e.g. an earlier editor/browser session) returns "not authorised".
  // So: try update; on ANY failure (or when there's no row id yet) INSERT a fresh
  // COMPLETE override = existing override's data ⊕ this change. Inserts are always
  // allowed, and newest-wins dedup makes the new row take precedence on read.
  // (Custom rows can't be safely re-created, so for them we try-and-skip.)
  const persistChange = useCallback(
    async (item: ManagedItem, change: Record<string, any>) => {
      // Stamp a client write time INTO the data so newest-wins dedup (recency())
      // is reliable regardless of whether the backend returns updatedAt.
      change = { ...change, _savedAt: Date.now() };
      const rid = resolveRowId(item);

      // Both default AND custom cards persist changes as OVERRIDE rows keyed by
      // the card's stable _mcId (defaults: a code default's id; customs: their
      // kind:"custom" base row id). We write the COMPLETE merged override
      // (existing merged data ⊕ this change) to a single keeper row — an
      // in-place update when we own the row, else a fresh insert (the platform
      // rejects cross-session UPDATEs/DELETEs but always allows INSERTs, and
      // newest-wins dedup makes the insert take precedence). This is why
      // hide/delete/edit now work on admin-added cards too. Writing the FULL
      // merged data (not just `change`) means the keeper alone holds everything,
      // so pruning the card's other rows afterward is safe.
      const merged = { ...overrideData(overrides[item._mcId]), ...change };
      // Encode cleared fields so the API cannot drop them (see CLEARED_SENTINEL).
      // Meta keys are left alone — they are never "cleared" by an admin edit.
      const WRITE_META = new Set(["kind", "refId", "order", "xorder", "hidden", "removed", "_savedAt", "_optKey"]);
      const full: Record<string, any> = {};
      for (const [k, v] of Object.entries(merged)) {
        full[k] = !WRITE_META.has(k) && (v === "" || v === null) ? CLEARED_SENTINEL : v;
      }
      let keepId: string | null = null;
      // True when the keeper row holds the COMPLETE merged override. If we had
      // to fall back to a MINIMAL insert (see below), the keeper only carries
      // this edit's changed fields — the card's older override rows must then
      // be KEPT, because they still supply every field this edit didn't touch.
      let keeperIsFull = true;
      if (rid) {
        try {
          await update(rid, full);
          keepId = String(rid);
        } catch {
          /* fall through to insert */
        }
      }
      if (!keepId) {
        try {
          const res = await insert({ ...full, kind: "override", refId: item._mcId });
          rememberRowId(item._mcId, res);
          const nid = res && ((res as any).id ?? (res as any)._id ?? (res as any).insertedId ?? (res as any).rowId);
          keepId = nid != null && String(nid) ? String(nid) : null;
        } catch (err) {
          // The FULL merged override was rejected (e.g. "Validation failed").
          // The admin's actual change is usually perfectly valid — the
          // rejection can come from some field in the OLDER merged data
          // carried along with it. Retry once with a MINIMAL payload: just
          // the meta keys plus the fields changed in THIS edit. On read, the
          // card's base/default data and its older override rows still
          // supply every other field, so nothing is lost — and the write goes
          // through. (Verified against the live API: minimal-shaped inserts
          // succeed even when they carry a fresh image value.)
          console.warn(
            "[useManagedCollection] full override insert rejected, retrying with minimal payload:",
            err,
          );
          try {
            const minimal: Record<string, any> = { kind: "override", refId: item._mcId };
            for (const [k, v] of Object.entries(change)) {
              minimal[k] = !WRITE_META.has(k) && (v === "" || v === null) ? CLEARED_SENTINEL : v;
            }
            const res = await insert(minimal);
            rememberRowId(item._mcId, res);
            const nid = res && ((res as any).id ?? (res as any)._id ?? (res as any).insertedId ?? (res as any).rowId);
            keepId = nid != null && String(nid) ? String(nid) : null;
            keeperIsFull = false;
          } catch (err2) {
            // Nothing reached the database by either path. Drop this card's
            // optimistic state so the admin sees the REAL stored value rather
            // than a phantom edit that exists only in this browser, and say
            // so out loud. Silently keeping the optimistic value here is
            // precisely what made the admin page and the public page
            // disagree indefinitely.
            setOpt((p) => {
              const n = { ...p };
              delete n[item._mcId];
              return n;
            });
            notifySaveFailed(item, err2);
            throw err2;
          }
        }
      }
      // Trim this card's now-redundant older override rows (best-effort) —
      // but ONLY when the keeper holds the complete merge. A minimal keeper
      // depends on the older rows for every unchanged field.
      if (keepId && keeperIsFull) {
        await pruneOverrides(item._mcId, keepId);
      }
    },
    [update, insert, overrides, resolveRowId, rememberRowId, pruneOverrides]
  );

  const saveEdit = useCallback(
    async (item: ManagedItem, fields: Record<string, any>) => {
      setOpt((p) => ({ ...p, [item._mcId]: { ...p[item._mcId], ...fields } })); // instant
      // Pin the card's CURRENT position into the edit so an edit can never
      // reorder it. Normally the override-merge carries `order` forward, but on
      // the platform an edit becomes a fresh INSERT (cross-session UPDATE is
      // rejected) and if a backend read doesn't surface the older order row the
      // card would fall back to its natural index and jump (often to the end).
      // Writing the effective order (and xorder, for the Restaurants grid)
      // alongside the edited fields guarantees the position sticks.
      const pos: Record<string, any> = {};
      if (Number.isFinite(item._order)) pos.order = item._order;
      const xo = Number((item as any).xorder);
      if (Number.isFinite(xo)) pos.xorder = xo;
      await persistChange(item, { ...pos, ...fields });
    },
    [persistChange]
  );

  const toggleHidden = useCallback(
    async (item: ManagedItem) => {
      const next = !item._hidden;
      setOpt((p) => ({ ...p, [item._mcId]: { ...p[item._mcId], _hidden: next } })); // instant
      await persistChange(item, { hidden: next });
    },
    [persistChange]
  );

  const deleteItem = useCallback(
    async (item: ManagedItem) => {
      setOpt((p) => ({ ...p, [item._mcId]: { ...p[item._mcId], _removed: true } })); // instant
      // Soft-delete via a removed:true OVERRIDE — works for BOTH default and
      // custom cards and is cross-session safe (an insert, which the platform
      // always allows). This is what makes deleting an admin-added card stick
      // (previously it remove()'d the base row, which the platform blocks
      // cross-session). We deliberately do NOT physically delete a custom card's
      // base row: keeping it means the optimistic-add entry stays deduped (so a
      // just-added card doesn't pop back as a pending item), the card shows the
      // "DELETED" badge in admin, is gone from the public site, and is
      // restorable — exactly like a default card.
      await persistChange(item, { removed: true });
    },
    [persistChange]
  );

  const restoreItem = useCallback(
    async (item: ManagedItem) => {
      setOpt((p) => ({ ...p, [item._mcId]: { ...p[item._mcId], _removed: false, _hidden: false } })); // instant
      await persistChange(item, { removed: false, hidden: false });
    },
    [persistChange]
  );

  // Reset a default item's fields back to the code default. Any admin
  // override (edit/image/etc.) that's currently applied gets overwritten by
  // the default's own values — a stuck old image from a prior edit is exactly
  // the case this recovers from. Admin-added (custom) cards have no code
  // default to fall back to, so this is a no-op for them.
  const resetToDefault = useCallback(
    async (item: ManagedItem, fieldKeys?: string[]) => {
      if (item._isCustom) return;
      const idx = (defaults || []).findIndex((d, i) => getDefaultId(d, i) === item._mcId);
      if (idx === -1) return;
      const def = (defaults[idx] || {}) as Record<string, any>;
      // Reset every relevant field key: explicit fieldKeys wins, else fall back
      // to the union of the default's own keys and the current item's keys (so
      // a field added only via admin edit still gets cleared).
      const keys =
        fieldKeys && fieldKeys.length
          ? fieldKeys
          : Array.from(new Set([...Object.keys(def), ...Object.keys(item)]));
      const resetVals: Record<string, any> = {};
      for (const k of keys) {
        if (k.startsWith("_")) continue; // skip ManagedItem meta fields
        resetVals[k] = def[k] !== undefined ? def[k] : "";
      }
      setOpt((p) => ({ ...p, [item._mcId]: { ...p[item._mcId], ...resetVals } })); // instant
      const pos: Record<string, any> = {};
      if (Number.isFinite(item._order)) pos.order = item._order;
      const xo = Number((item as any).xorder);
      if (Number.isFinite(xo)) pos.xorder = xo;
      await persistChange(item, { ...pos, ...resetVals });
    },
    [defaults, getDefaultId, persistChange]
  );

  // Persist a new sort position for one item (custom row or default override).
  // Uses the SDK's NATIVE insert/update (which auto-refetches + re-renders) —
  // the same flow add/edit/hide use, so the UI updates live on the platform too.
  const persistOrder = useCallback(
    async (item: ManagedItem, order: number) => {
      await persistChange(item, { order });
    },
    [persistChange]
  );

  // Drag-sort: drop `draggedId` onto `targetId`. We anchor to the target and its
  // neighbour in the full ordered list and give the dragged item a midpoint order
  // (fractional ranking) — so each drop is a single write and never renumbers the
  // whole list. Anchoring to the target keeps it correct even when filtered.
  const moveItem = useCallback(
    async (draggedId: string, targetId: string, before = false) => {
      if (draggedId === targetId) return;
      const ordered = allItems; // already sorted by _order
      const dragged = ordered.find((i) => i._mcId === draggedId);
      const target = ordered.find((i) => i._mcId === targetId);
      if (!dragged || !target) return;
      // Compute neighbours in the list WITH the dragged item removed, then drop
      // it into the requested gap (before/after target) via a midpoint order.
      const without = ordered.filter((i) => i._mcId !== draggedId);
      const tIdx = without.findIndex((i) => i._mcId === targetId);
      if (tIdx === -1) return;
      let newOrder: number;
      if (before) {
        const prev = without[tIdx - 1];
        newOrder = prev ? (prev._order + target._order) / 2 : target._order - 1;
      } else {
        const nxt = without[tIdx + 1];
        newOrder = nxt ? (target._order + nxt._order) / 2 : target._order + 1;
      }
      // No-op guard: if the dragged item is already exactly where requested.
      if (newOrder === dragged._order) return;
      setOpt((p) => ({ ...p, [draggedId]: { ...p[draggedId], _order: newOrder } })); // instant
      await persistOrder(dragged, newOrder);
    },
    [allItems, persistOrder]
  );

  // SWAP: the two items exchange order values; everything else is untouched.
  // Because we only swap two existing distinct orders, no collisions or drift.
  const swapItems = useCallback(
    async (aId: string, bId: string) => {
      if (aId === bId) return;
      const a = allItems.find((i) => i._mcId === aId);
      const b = allItems.find((i) => i._mcId === bId);
      if (!a || !b) return;
      // Sequential native writes — each one refetches + re-renders. Sequential
      // (not Promise.all) so the two refetches can't race and drop an update.
      const aOrder = a._order;
      const bOrder = b._order;
      // Optimistic: swap instantly in the UI, then persist in the background.
      setOpt((p) => ({
        ...p,
        [aId]: { ...p[aId], _order: bOrder },
        [bId]: { ...p[bId], _order: aOrder },
      }));
      await persistOrder(a, bOrder);
      await persistOrder(b, aOrder);
    },
    [allItems, persistOrder]
  );

  return {
    slug,
    items,
    visibleCount: visibleItems.length,
    totalCount: allItems.length,
    isAdmin,
    adminMode,
    loading,
    refetch,
    addItem,
    saveEdit,
    deleteItem,
    toggleHidden,
    restoreItem,
    resetToDefault,
    moveItem,
    swapItems,
  };
}
